import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton,
  Alert, TextField,
  Stack, Paper, FormControl, Chip, Tabs, Tab, Select, MenuItem, InputLabel, Snackbar,
  FormControlLabel, Checkbox, Divider, useTheme, alpha
} from '@mui/material';
import { Plus, Trash2, Edit, GripVertical, X, CreditCard } from 'lucide-react';
import axios from '../utils/axios';
import PageHeader from '../components/common/PageHeader';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions, DeleteConfirmDialog } from '../components/common/modal';
import EmptyState from '../components/common/EmptyState';
import LoadingState from '../components/common/LoadingState';
import ExportTableButton from '../components/common/ExportTableButton';

// DND Kit Imports
import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// Sortable Item Component
const SortableFeature = ({ feature, onRemove, onUpdate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: feature.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative'
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ mb: 1.5 }}>
      <Paper 
        elevation={isDragging ? 4 : 0}
        sx={{ 
          p: 1.5, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5, 
          bgcolor: isDragging ? 'background.paper' : 'background.neutral',
          border: '1px solid',
          borderColor: isDragging ? 'info.main' : 'divider',
          borderRadius: 2
        }}
      >
        <Box {...attributes} {...listeners} sx={{ cursor: 'grab', color: '#94a3b8', display: 'flex' }}>
          <GripVertical size={20} />
        </Box>
        
        <FormControl fullWidth variant="standard">
          <TextField 
            variant="standard" 
            fullWidth 
            value={feature.name} 
            placeholder="Feature name (e.g. 10 Business Listings)"
            onChange={(e) => onUpdate(feature.id, 'name', e.target.value.trimStart())}
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem', fontWeight: 600 } }}
          />
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" fontWeight={700} color={feature.status ? 'success.main' : 'text.disabled'}>
            {feature.status ? 'ACTIVE' : 'INACTIVE'}
          </Typography>
          <Box 
            onClick={() => onUpdate(feature.id, 'status', !feature.status)}
            sx={{ 
              width: 32, height: 18, borderRadius: 10, bgcolor: feature.status ? 'success.main' : '#94a3b8',
              cursor: 'pointer', position: 'relative', transition: 'all 0.2s ease'
            }}
          >
            <Box sx={{ 
              width: 14, height: 14, borderRadius: '50%', bgcolor: 'white',
              position: 'absolute', top: 2, left: feature.status ? 16 : 2,
              transition: 'all 0.2s ease'
            }} />
          </Box>
        </Box>

        <IconButton aria-label="Remove feature" size="small" onClick={() => onRemove(feature.id)} sx={{ color: 'error.main', ml: 1 }}>
          <X size={18} />
        </IconButton>
      </Paper>
    </Box>
  );
};

const PlansView = () => {
  const theme = useTheme();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // 'subscription' | 'promotion' | 'lead_topup'
  const [activeTab, setActiveTab] = useState('subscription');

  // A plan belongs to exactly one tab. Legacy rows predate plan_type having more
  // than two values, so anything not explicitly a promotion or a top-up is a
  // subscription. Without this, top-up packs would show under Subscriptions and
  // could be edited with the wrong fields.
  const planTabOf = (plan) =>
    plan?.plan_type === 'promotion'
      ? 'promotion'
      : plan?.plan_type === 'lead_topup'
        ? 'lead_topup'
        : 'subscription';

  const TAB_META = {
    subscription: {
      title: 'Subscription Plans',
      countLabel: 'plans',
      addLabel: 'Subscription',
      blurb: 'Manage pricing tiers (Free, Basic, Starter, Pro) for business listings.',
    },
    promotion: {
      title: 'Product Promotion Plans',
      countLabel: 'promotions',
      addLabel: 'Promotion',
      blurb: 'Manage paid promotions (Boost Product, Homepage Trending) for individual products.',
    },
    lead_topup: {
      title: 'Lead Top-up Packs',
      countLabel: 'packs',
      addLabel: 'Top-up',
      blurb: 'One-off packs of extra leads added to a seller’s active plan. Keep the price per lead HIGHER than your cheapest subscription, or sellers will buy top-ups instead of subscribing.',
    },
  };
  const tabMeta = TAB_META[activeTab] || TAB_META.subscription;
  
  // Modal states
  const [open, setOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [formData, setFormData] = useState({
    plan_name: '',
    plan_price: '',
    product_listing: '',
    plan_duration: '',
    description: '',
    popular: false,
    plan_type: 'subscription', // 'subscription' | 'promotion'
    promo_type: '', // 'boost_3_days' | 'homepage_trending' | ''
    // Lead quota & entitlements (subscription plans)
    lead_quota: '',
    lead_quota_unlimited: false,
    featureProduct_qty: '',
    grant_verified_seller: false,
    grant_trusted_seller: false,
    daily_lead_cap: '',
    ranking_boost: '',
    features: [], // Array of { id, name, status }
    duration_tiers: [] // Array of { id, days, discount } — promotion plans only
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/plans');
      setPlans(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleOpen = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        plan_name: plan.plan_name || '',
        plan_price: plan.plan_price !== undefined && plan.plan_price !== null ? String(plan.plan_price) : '',
        product_listing: plan.product_listing || '',
        plan_duration: plan.plan_duration || '',
        description: plan.description || '',
        popular: !!plan.popular,
        plan_type: plan.plan_type || 'subscription',
        promo_type: plan.promo_type || '',
        lead_quota: plan.lead_quota ?? '',
        lead_quota_unlimited: !!plan.lead_quota_unlimited,
        featureProduct_qty: plan.featureProduct_qty ?? '',
        grant_verified_seller: !!plan.grant_verified_seller,
        grant_trusted_seller: !!plan.grant_trusted_seller,
        daily_lead_cap: plan.daily_lead_cap ?? '',
        ranking_boost: plan.ranking_boost ?? '',
        features: plan.features?.map((f, index) => ({
          id: `feature-${index}-${Date.now()}`,
          name: typeof f === 'object' ? f.name : f,
          status: typeof f === 'object' ? f.status !== false : true
        })) || [],
        duration_tiers: (plan.duration_tiers || []).map((t, index) => ({
          id: `tier-${index}-${Date.now()}`,
          days: t.days ?? '',
          discount: t.discount ?? 0
        }))
      });
    } else {
      setEditingPlan(null);
      setFormData({
        plan_name: '', plan_price: '', product_listing: '',
        plan_duration: '', description: '', popular: false,
        plan_type: activeTab,
        promo_type: '',
        lead_quota: '', lead_quota_unlimited: false, featureProduct_qty: '',
        grant_verified_seller: false, grant_trusted_seller: false,
        daily_lead_cap: '', ranking_boost: '',
        features: [],
        duration_tiers: []
      });
    }
    setSaveError(null);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  // Feature Handlers
  const addFeature = () => {
    const newFeature = { id: `feature-new-${Date.now()}`, name: '', status: true };
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, newFeature]
    }));
  };

  const removeFeature = (id) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(f => f.id !== id)
    }));
  };

  const updateFeatureText = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map(f => f.id === id ? { ...f, [field]: value } : f)
    }));
  };

  // Duration tier handlers (promotion plans only)
  const addTier = () => {
    setFormData(prev => ({
      ...prev,
      duration_tiers: [...prev.duration_tiers, { id: `tier-new-${Date.now()}`, days: '', discount: 0 }]
    }));
  };

  const removeTier = (id) => {
    setFormData(prev => ({
      ...prev,
      duration_tiers: prev.duration_tiers.filter(t => t.id !== id)
    }));
  };

  const updateTier = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      duration_tiers: prev.duration_tiers.map(t => t.id === id ? { ...t, [field]: value } : t)
    }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFormData(prev => {
        const oldIndex = prev.features.findIndex(f => f.id === active.id);
        const newIndex = prev.features.findIndex(f => f.id === over.id);
        return {
          ...prev,
          features: arrayMove(prev.features, oldIndex, newIndex)
        };
      });
    }
  };

  const handleSubmit = async () => {
    if (saving) return; // guard against double-submit
    setSaving(true);
    setSaveError(null);
    try {
      const data = {
        ...formData,
        plan_name: formData.plan_name.trim(),
        description: formData.description.trim(),
        plan_price: formData.plan_price === '' || formData.plan_price === null || formData.plan_price === undefined ? 0 : Number(formData.plan_price),
        // Coerce lead-quota / entitlement numerics (empty -> 0).
        lead_quota: Number(formData.lead_quota) || 0,
        lead_quota_unlimited: !!formData.lead_quota_unlimited,
        featureProduct_qty: Number(formData.featureProduct_qty) || 0,
        daily_lead_cap: Number(formData.daily_lead_cap) || 0,
        ranking_boost: Number(formData.ranking_boost) || 0,
        grant_verified_seller: !!formData.grant_verified_seller,
        grant_trusted_seller: !!formData.grant_trusted_seller,
        features: formData.features.map(f => ({ name: f.name.trim(), status: f.status })),
        duration_tiers: formData.plan_type === 'promotion'
          ? formData.duration_tiers
              .filter(t => Number(t.days) > 0)
              .map(t => ({ days: Number(t.days), discount: Number(t.discount) || 0 }))
          : []
      };

      // A top-up is only a price and a lead count. Pin everything else off so a
      // pack can never be read as granting a badge, a featured slot or unlimited
      // leads, whatever the form happened to be holding.
      if (data.plan_type === 'lead_topup') {
        data.lead_quota_unlimited = false;
        data.featureProduct_qty = 0;
        data.daily_lead_cap = 0;
        data.ranking_boost = 0;
        data.grant_verified_seller = false;
        data.grant_trusted_seller = false;
        data.plan_duration = 0;
        data.popular = false;
      }

      // Plans with price 0 or 29 or named Free are Lifetime Free (duration 0).
      if (
        data.plan_type === 'subscription' &&
        (Number(data.plan_price) === 0 || Number(data.plan_price) === 29 || (data.plan_name || '').toLowerCase().includes('free'))
      ) {
        data.plan_duration = 0;
      }

      if (data.plan_type === 'lead_topup' && data.lead_quota < 1) {
        setSaveError('A lead top-up pack must grant at least 1 lead.');
        setSaving(false);
        return;
      }

      if (editingPlan) {
        await axios.put(`/admin/plans/${editingPlan._id}`, data, { skipNotify: 'success' });
      } else {
        await axios.post('/admin/plans', data, { skipNotify: 'success' });
      }
      fetchPlans();
      handleClose();
      setSnackbar({ open: true, message: editingPlan ? 'Plan updated successfully' : 'Plan created successfully', severity: 'success' });
    } catch (err) {
      // Keep this error inside the dialog so it doesn't blank the plans table.
      setSaveError(err.response?.data?.msg || 'Failed to save plan. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteDialog({ open: true, id });

  const confirmDelete = async () => {
    const { id } = deleteDialog;
    if (!id) return;
    try {
      await axios.delete(`/admin/plans/${id}`, { skipNotify: 'success' });
      fetchPlans();
      setSnackbar({ open: true, message: 'Plan deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete plan. Please try again.', severity: 'error' });
    }
    setDeleteDialog({ open: false, id: null });
  };

  const filteredPlans = (Array.isArray(plans) ? plans : []).filter(plan => planTabOf(plan) === activeTab);

  return (
    <Box>
      <PageHeader
        title={tabMeta.title}
        subtitle={tabMeta.blurb}
        count={filteredPlans.length}
        countLabel={tabMeta.countLabel}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Subscriptions', href: '/plans' },
          { label: tabMeta.title }
        ]}
        actionLabel={`Add ${tabMeta.addLabel} Plan`}
        actionIcon={<Plus size={18} />}
        onAction={() => handleOpen()}
        secondaryAction={
          <ExportTableButton
            data={filteredPlans}
            total={filteredPlans.length}
            columns={[
              { label: 'Plan Name', key: 'plan_name' },
              { label: 'Price (₹)', key: 'plan_price' },
              { label: 'Duration (Days)', key: 'plan_duration' },
              { label: 'Type', value: (r) => r.plan_type || 'subscription' },
              { label: 'Active', value: (r) => r.is_active ? 'Yes' : 'No' },
              { label: 'Popular', value: (r) => r.is_popular ? 'Yes' : 'No' },
              { label: 'Lead Quota', value: (r) => r.lead_quota_unlimited ? 'Unlimited' : (r.lead_quota_total ?? 'N/A') },
            ]}
            filename={`plans_${activeTab}`}
          />
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              fontWeight: 700,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
            }
          }}
        >
          <Tab label="Subscription Plans" value="subscription" />
          <Tab label="Product Promotion Plans" value="promotion" />
          <Tab label="Lead Top-ups" value="lead_topup" />
        </Tabs>
      </Box>

      <Card sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        {error && <Alert severity="error" sx={{ m: 2.5 }}>{error}</Alert>}
        
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>Plan Name</TableCell>
                <TableCell>Price (₹)</TableCell>
                <TableCell>Duration</TableCell>
                {activeTab === 'subscription' ? (
                  <TableCell>Popular</TableCell>
                ) : (
                  <TableCell>Promotion Type</TableCell>
                )}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <LoadingState rows={5} cols={5} />
              ) : filteredPlans.length > 0 ? (
                filteredPlans.map((plan) => (
                  <TableRow key={plan._id} hover>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{plan.plan_name}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {Number(plan.plan_price) === 0
                        ? '₹0 (Free)'
                        : Number(plan.plan_price) === 29
                        ? '₹29 (Lifetime Free)'
                        : `₹${plan.plan_price}`}
                    </TableCell>
                    <TableCell>
                      {Number(plan.plan_duration) === 0 || Number(plan.plan_price) === 0 || Number(plan.plan_price) === 29 || (plan.plan_name || '').toLowerCase().includes('free')
                        ? 'Lifetime Free'
                        : `${plan.plan_duration || 30} Days`}
                    </TableCell>
                    {activeTab === 'subscription' ? (
                      <TableCell>
                        <Chip 
                          label={plan.popular ? 'POPULAR' : 'STANDARD'} 
                          size="small" 
                          color={plan.popular ? 'warning' : 'default'}
                          variant={plan.popular ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 700, fontSize: '0.6875rem' }} 
                        />
                      </TableCell>
                    ) : (
                      <TableCell>
                        <Chip 
                          label={plan.promo_type === 'boost_3_days' ? 'BOOST PRODUCT' : plan.promo_type === 'homepage_trending' ? 'HOMEPAGE TRENDING' : 'CUSTOM PROMO'} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 700, fontSize: '0.6875rem' }} 
                        />
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <IconButton aria-label="Edit" onClick={() => handleOpen(plan)} color="primary" size="small">
                        <Edit size={18} />
                      </IconButton>
                      <IconButton aria-label="Delete" onClick={() => handleDelete(plan._id)} color="error" size="small">
                        <Trash2 size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                    <EmptyState
                      icon={CreditCard}
                      title={`No ${tabMeta.title.toLowerCase()} configured`}
                      description="Create and publish your first tier to begin monetizing merchant listings and services."
                      actionLabel={`Add ${tabMeta.addLabel} Plan`}
                      onAction={() => handleOpen()}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <AdminDialog open={open} onClose={handleClose} maxWidth="sm">
        <AdminDialogHeader
          title={editingPlan ? 'Edit Plan' : 'Create New Plan'}
          subtitle="Configure plan details, pricing tiers, and quota features"
          icon={CreditCard}
          onClose={handleClose}
        />
        <AdminDialogContent dividers>
          <Stack spacing={4}>
            {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
            {/* Classification */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <FormControl fullWidth variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                <InputLabel id="plan-type-label">Plan Type</InputLabel>
                <Select
                  labelId="plan-type-label"
                  label="Plan Type"
                  value={formData.plan_type}
                  onChange={(e) => setFormData({...formData, plan_type: e.target.value, promo_type: e.target.value === 'subscription' ? '' : formData.promo_type})}
                  sx={{ borderRadius: 2.5 }}
                >
                  <MenuItem value="subscription">Subscription Plan</MenuItem>
                  <MenuItem value="promotion">Product Promotion Plan</MenuItem>
                  <MenuItem value="lead_topup">Lead Top-up Pack</MenuItem>
                </Select>
              </FormControl>

              {formData.plan_type === 'promotion' ? (
                <FormControl fullWidth variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                  <InputLabel id="promo-type-label">Promotion Target</InputLabel>
                  <Select
                    labelId="promo-type-label"
                    label="Promotion Target"
                    value={formData.promo_type}
                    onChange={(e) => setFormData({...formData, promo_type: e.target.value})}
                    sx={{ borderRadius: 2.5 }}
                  >
                    <MenuItem value="">Custom / No Target</MenuItem>
                    <MenuItem value="boost_3_days">Boost Product (3 Days Verification)</MenuItem>
                    <MenuItem value="homepage_trending">Homepage Trending (7 Days Verification)</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Box sx={{
                  p: 2, borderRadius: 2.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Popular Plan</Typography>
                    <Typography variant="caption" color="text.secondary">Highlight on landing page</Typography>
                  </Box>
                  <Box 
                    onClick={() => setFormData({...formData, popular: !formData.popular})}
                    sx={{ 
                      width: 44, height: 24, borderRadius: 10, bgcolor: formData.popular ? 'info.main' : '#94a3b8',
                      cursor: 'pointer', position: 'relative', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <Box sx={{ 
                      width: 18, height: 18, borderRadius: '50%', bgcolor: 'white',
                      position: 'absolute', top: 3, left: formData.popular ? 23 : 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </Box>
                </Box>
              )}
            </Box>

            {/* Header Settings */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <TextField 
                label="Plan Name" 
                fullWidth 
                variant="outlined"
                value={formData.plan_name} 
                onChange={(e) => setFormData({...formData, plan_name: e.target.value.trimStart()})} 
                sx={{ bgcolor: 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
              />
              <TextField 
                label="Price (₹)" 
                type="number" 
                fullWidth 
                variant="outlined"
                value={formData.plan_price} 
                onChange={(e) => setFormData({...formData, plan_price: e.target.value})} 
                sx={{ bgcolor: 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <TextField 
                label="Duration (Days)" 
                type="number" 
                fullWidth 
                variant="outlined"
                value={formData.plan_duration} 
                onChange={(e) => setFormData({...formData, plan_duration: e.target.value})} 
                helperText="Set 0 for Lifetime Validity"
                sx={{ bgcolor: 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
              />
            </Box>

            {/* A top-up pack needs ONLY the lead count — the other entitlement
                fields are meaningless for it, and leaving this whole block gated
                to 'subscription' would have hidden Lead Quota while editing a
                pack, silently saving it back as 0 leads. */}
            {(formData.plan_type === 'subscription' || formData.plan_type === 'lead_topup') && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                  {formData.plan_type === 'lead_topup' ? 'Leads In This Pack' : 'Lead Quota & Entitlements'}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                  <TextField
                    label={formData.plan_type === 'lead_topup' ? 'Leads per purchase' : 'Lead Quota (leads per purchase)'}
                    type="number"
                    fullWidth
                    disabled={formData.plan_type === 'subscription' && formData.lead_quota_unlimited}
                    value={formData.lead_quota}
                    onChange={(e) => setFormData({ ...formData, lead_quota: e.target.value })}
                    helperText={
                      formData.plan_type === 'lead_topup'
                        ? `Extra leads credited on purchase.${
                            Number(formData.lead_quota) > 0 && Number(formData.plan_price) > 0
                              ? ` That is ₹${Math.round(Number(formData.plan_price) / Number(formData.lead_quota))} per lead — keep this ABOVE your cheapest plan's rate.`
                              : ''
                          }`
                        : 'How many leads this plan grants the seller.'
                    }
                  />
                  {formData.plan_type === 'subscription' && (
                    <>
                      <TextField
                        label="Featured Products"
                        type="number"
                        fullWidth
                        value={formData.featureProduct_qty}
                        onChange={(e) => setFormData({ ...formData, featureProduct_qty: e.target.value })}
                      />
                      <TextField
                        label="Daily Lead Cap (0 = global default)"
                        type="number"
                        fullWidth
                        value={formData.daily_lead_cap}
                        onChange={(e) => setFormData({ ...formData, daily_lead_cap: e.target.value })}
                      />
                      <TextField
                        label="Ranking Boost"
                        type="number"
                        fullWidth
                        value={formData.ranking_boost}
                        onChange={(e) => setFormData({ ...formData, ranking_boost: e.target.value })}
                        helperText="Added to seller score in lead matching."
                      />
                    </>
                  )}
                </Box>
                {formData.plan_type === 'subscription' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <FormControlLabel
                        control={<Checkbox checked={formData.lead_quota_unlimited}
                          onChange={(e) => setFormData({ ...formData, lead_quota_unlimited: e.target.checked })} />}
                        label="Unlimited leads"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={formData.grant_trusted_seller}
                          onChange={(e) => setFormData({ ...formData, grant_trusted_seller: e.target.checked })} />}
                        label="Grant Trusted badge"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={formData.grant_verified_seller}
                          onChange={(e) => setFormData({ ...formData, grant_verified_seller: e.target.checked })} />}
                        label="Grant Verified badge"
                      />
                    </Stack>
                  </>
                )}
              </Box>
            )}

            <TextField
              label="Description"
              multiline
              rows={2}
              fullWidth
              variant="outlined"
              placeholder="Give a short summary of this plan..."
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value.trimStart()})}
              sx={{ bgcolor: 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
            />

            {formData.plan_type === 'promotion' && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>DURATION TIERS</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Offer this promo at multiple durations. Price = per-day rate × days × (1 − discount %).
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Plus size={16} />}
                    onClick={addTier}
                    sx={{ borderRadius: 2, fontWeight: 700, borderColor: '#f75d34', color: '#f75d34', '&:hover': { bgcolor: alpha('#f75d34', 0.05), borderColor: '#f75d34' } }}
                  >
                    Add Tier
                  </Button>
                </Box>

                <Stack spacing={1.5}>
                  {formData.duration_tiers.map((tier) => {
                    const basePrice = Number(formData.plan_price) || 0;
                    const baseDays = Number(formData.plan_duration) || 0;
                    const days = Number(tier.days) || 0;
                    const discount = Number(tier.discount) || 0;
                    const previewPrice = basePrice && baseDays && days
                      ? Math.round((basePrice / baseDays) * days * (1 - discount / 100))
                      : 0;
                    return (
                      <Paper
                        key={tier.id}
                        elevation={0}
                        sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.1), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`, borderRadius: 2 }}
                      >
                        <TextField
                          label="Days"
                          type="number"
                          size="small"
                          value={tier.days}
                          onChange={(e) => updateTier(tier.id, 'days', e.target.value)}
                          inputProps={{ min: 1 }}
                          sx={{ width: 120, bgcolor: 'background.paper' }}
                        />
                        <TextField
                          label="Discount %"
                          type="number"
                          size="small"
                          value={tier.discount}
                          onChange={(e) => updateTier(tier.id, 'discount', e.target.value)}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{ width: 140, bgcolor: 'background.paper' }}
                        />
                        <Box sx={{ flexGrow: 1, textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Will charge
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={800} color="#f75d34">
                            ₹{previewPrice}
                          </Typography>
                        </Box>
                        <IconButton aria-label="Remove tier" size="small" onClick={() => removeTier(tier.id)} sx={{ color: 'error.main' }}>
                          <X size={18} />
                        </IconButton>
                      </Paper>
                    );
                  })}
                  {formData.duration_tiers.length === 0 && (
                    <Box sx={{ py: 4, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7 }}>
                        No duration tiers configured. Buyers will be billed the base price for {formData.plan_duration || 'N/A'} days.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>PLAN FEATURES</Typography>
                  <Typography variant="caption" color="text.secondary">Drag to reorder items</Typography>
                </Box>
                <Button 
                  size="small" 
                  variant="outlined"
                  startIcon={<Plus size={16} />} 
                  onClick={addFeature}
                  sx={(theme) => ({ borderRadius: 2, fontWeight: 700, borderColor: 'info.main', color: 'info.main', '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.05), borderColor: 'info.main' } })}
                >
                  Add Feature
                </Button>
              </Box>
              
              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext 
                  items={formData.features.map(f => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Box sx={{ minHeight: 40, mt: 1 }}>
                    {formData.features.map((feature) => (
                      <SortableFeature 
                        key={feature.id} 
                        feature={feature} 
                        onRemove={removeFeature}
                        onUpdate={updateFeatureText}
                      />
                    ))}
                    {formData.features.length === 0 && (
                      <Box sx={{ 
                        py: 5, 
                        textAlign: 'center', 
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 3,
                        bgcolor: 'background.paper'
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7 }}>No features added to this plan.</Typography>
                      </Box>
                    )}
                  </Box>
                </SortableContext>
              </DndContext>
            </Box>
          </Stack>
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={handleClose} color="inherit" disabled={saving} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={saving} sx={{ px: 3, fontWeight: 700 }}>
            {saving ? 'Saving...' : (editingPlan ? 'Save Plan' : 'Create Plan')}
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      <DeleteConfirmDialog
        open={deleteDialog.open}
        itemType="Plan"
        description="Are you sure you want to delete this subscription plan? Subscribers or listings linked may be affected."
        onConfirm={confirmDelete}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlansView;
